import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const API_URL = 'https://social-auto-r8we.onrender.com/api/whatsapp';

// Get all WhatsApp accounts
export const getWhatsAppAccounts = createAsyncThunk(
    'whatsapp/getAccounts',
    async (_, { getState, rejectWithValue }) => {
        try {
            const { token } = getState().auth;
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const response = await axios.get(`${API_URL}/accounts`, config);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

// Add WhatsApp account
export const addWhatsAppAccount = createAsyncThunk(
    'whatsapp/addAccount',
    async (phoneNumber, { getState, rejectWithValue }) => {
        try {
            const { token } = getState().auth;
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const response = await axios.post(`${API_URL}/accounts`,
                { phoneNumber },
                config
            );
            return response.data.account;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

// Remove WhatsApp account
export const removeWhatsAppAccount = createAsyncThunk(
    'whatsapp/removeAccount',
    async (accountId, { getState, rejectWithValue }) => {
        try {
            const { token } = getState().auth;
            const config = { headers: { Authorization: `Bearer ${token}` } };
            await axios.delete(`${API_URL}/accounts/${accountId}`, config);
            return accountId;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

// Get account status
export const getAccountStatus = createAsyncThunk(
    'whatsapp/getStatus',
    async (accountId, { getState, rejectWithValue }) => {
        try {
            const { token } = getState().auth;
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const response = await axios.get(`${API_URL}/accounts/${accountId}/status`, config);
            return { accountId, status: response.data };
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

// Initialize account (to get QR)
export const initializeAccount = createAsyncThunk(
    'whatsapp/initializeAccount',
    async (accountId, { getState, rejectWithValue }) => {
        try {
            const { token } = getState().auth;
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const response = await axios.post(`${API_URL}/accounts/${accountId}/initialize`, {}, config);
            return { accountId, ...response.data };
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

// Send message
export const sendWhatsAppMessage = createAsyncThunk(
    'whatsapp/sendMessage',
    async ({ accountId, number, text }, { getState, rejectWithValue }) => {
        try {
            const { token } = getState().auth;
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const response = await axios.post(`${API_URL}/send-message`,
                { accountId, number, text },
                config
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

const whatsappSlice = createSlice({
    name: 'whatsapp',
    initialState: {
        accounts: [],
        selectedAccount: null,
        qrCode: null,
        loading: false,
        error: null,
    },
    reducers: {
        setSelectedAccount: (state, action) => {
            state.selectedAccount = action.payload;
        },
        setQRCode: (state, action) => {
            state.qrCode = action.payload;
        },
        updateAccountStatus: (state, action) => {
            const { accountId, status } = action.payload;
            const account = state.accounts.find(a => a._id === accountId);
            if (account) {
                account.status = status;
            }
        },
        clearError: (state) => {
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // Get accounts
            .addCase(getWhatsAppAccounts.pending, (state) => {
                state.loading = true;
            })
            .addCase(getWhatsAppAccounts.fulfilled, (state, action) => {
                state.loading = false;
                state.accounts = action.payload;
            })
            .addCase(getWhatsAppAccounts.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Add account
            .addCase(addWhatsAppAccount.pending, (state) => {
                state.loading = true;
            })
            .addCase(addWhatsAppAccount.fulfilled, (state, action) => {
                state.loading = false;
                state.accounts.push(action.payload);
            })
            .addCase(addWhatsAppAccount.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Remove account
            .addCase(removeWhatsAppAccount.pending, (state) => {
                state.loading = true;
            })
            .addCase(removeWhatsAppAccount.fulfilled, (state, action) => {
                state.loading = false;
                state.accounts = state.accounts.filter(a => a._id !== action.payload);
            })
            .addCase(removeWhatsAppAccount.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Get status
            .addCase(getAccountStatus.fulfilled, (state, action) => {
                const { accountId, status } = action.payload;
                const account = state.accounts.find(a => a._id === accountId);
                if (account) {
                    Object.assign(account, status);
                }
            })
            // Send message
            .addCase(sendWhatsAppMessage.pending, (state) => {
                state.loading = true;
            })
            .addCase(sendWhatsAppMessage.fulfilled, (state) => {
                state.loading = false;
            })
            .addCase(sendWhatsAppMessage.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    }
});

export const { setSelectedAccount, setQRCode, updateAccountStatus, clearError } = whatsappSlice.actions;
export default whatsappSlice.reducer;
